import { ERROR_TYPE } from "@/utils/errors";
import { isPlanInactiveError, readApiErrorCode } from "@/utils/lifecycleErrors";

/**
 * The send refusals that have wording of their own, by their `ERROR_TYPE` code (PREC-4216): an
 * ordinary LinkedIn message or a new invitation to a lead whose invitation from this sender is
 * pending (the state changed between the composer's check and the click), a second identical
 * message parked behind the same invitation (from the direct route or the Inbox's deferred one),
 * and a batch whose messages cannot all wait behind one invitation.
 */
const SEND_REFUSAL_KEYS: Partial<Record<string, string>> = {
	[ERROR_TYPE.LINKEDIN_INVITATION_PENDING]: "inbox.pendingInvitation.refused",
	[ERROR_TYPE.PARKED_MESSAGE_DUPLICATE]: "inbox.pendingInvitation.duplicate",
	[ERROR_TYPE.INVITATION_BATCH_UNSUPPORTED]: "inbox.pendingInvitation.batchUnsupported",
};

/** The refusals caused by an invitation the composer did not know was pending. */
const PENDING_STATE_REFUSALS = new Set<string>([
	ERROR_TYPE.LINKEDIN_INVITATION_PENDING,
	ERROR_TYPE.INVITATION_BATCH_UNSUPPORTED,
]);

/**
 * The translation key for the toast a rejected send shows (PREC-3900).
 *
 * A cancelled company's send is refused with a 403 `PLAN_INACTIVE`, which is a lifecycle state
 * rather than a failure — it needs its own wording, because "try again" is wrong advice for a
 * customer whose subscription has ended and nothing they retry will go out. The pending-invitation
 * refusals say what happened and what to do instead (PREC-4216). Every other rejection falls back
 * to the generic message; the alternative is what shipped before, which was silence.
 */
export const sendFailureMessageKey = (error: unknown): string => {
	if (isPlanInactiveError(error)) {
		return "inbox.planInactive";
	}

	return SEND_REFUSAL_KEYS[readApiErrorCode(error) ?? ""] ?? "inbox.sendFailed";
};

/**
 * The API refused the send because an invitation is pending that the composer's state did not
 * show: an ordinary message or a new invitation from that sender, or a batch only a known pending
 * state would have planned differently. The composer had an older state, so it asks again; the
 * notice and "Send when accepted" then appear, and an invitation draft becomes a message that
 * waits (PREC-4216).
 */
export const isInvitationPendingRefusal = (error: unknown): boolean =>
	PENDING_STATE_REFUSALS.has(readApiErrorCode(error) ?? "");
